import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { BillingStatementService } from '../../../services/B2BServices';
import { B2BClientService } from '../../../services/B2BServices';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { formatMoney } from '../../../utils/currency';

const SendBillingStatementEmailModal = ({ open, onClose, statement, onSuccess }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchClientEmail = async () => {
      if (statement) {
        // Intentar obtener clientId de diferentes formas posibles
        // En algunos casos, el id del statement puede ser el clientId
        const clientId = statement.clientId || statement.id || statement.client?.id;
        
        if (clientId) {
          try {
            const client = await B2BClientService.getClientById(clientId);
            if (client && client.email) {
              setEmail(client.email);
            }
          } catch (error) {
            console.error('Error fetching client email:', error);
            // Si hay error, simplemente dejamos el campo vacío
          }
        }
      }
    };

    if (open && statement) {
      fetchClientEmail();
    } else {
      setEmail('');
      setError('');
    }
  }, [open, statement]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSend = async () => {
    setError('');

    if (!email || !email.trim()) {
      setError(t('email_required'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('invalid_email'));
      return;
    }

    if (!statement || !statement.id) {
      setError(t('billing_statement_not_found'));
      return;
    }

    try {
      setLoading(true);
      await BillingStatementService.sendBillingStatementEmail(statement.id, email.trim());
      SnackbarUtils.success(t('email_sent_successfully'));
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error sending billing statement email:', error);
      setError(error.response?.data?.message || t('error_sending_email'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon('mail')}
        {t('send_billing_statement_email')}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('send_billing_statement_email_description')}
          </Typography>

          <TextField
            fullWidth
            label={t('email')}
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            variant="outlined"
            disabled={loading}
            sx={{ mb: 2 }}
            helperText={t('email_helper_text')}
          />

          {statement && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('client')}:</strong> {statement.clientName || t('unassigned')}
              </Typography>
              {statement.pendingAmount && (
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('pending_amount')}:</strong> {formatMoney(statement.pendingAmount)}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          {t('cancel')}
        </Button>
        <Button 
          onClick={handleSend} 
          variant="contained" 
          disabled={loading || !email.trim()}
          startIcon={getIcon('mail')}
        >
          {loading ? t('sending') : t('send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendBillingStatementEmailModal;

